import { z } from 'zod';

import {
  BaseFlowBlockSchema,
  BlockType,
  DurationSchemaWithMax,
  DurationUnits,
  ProducedType,
  convertDurationToMilliseconds,
  returnTypeSchema,
} from '../base/baseSchema.js';
import {
  BlockValueFieldSchema,
  BlockValueFieldSchemaVariant,
} from '../base/blockValueFieldSchema.js';
import { BlockValueTemplateDurationSchema } from '../base/blockValueTemplateDurationSchema.js';

export enum WaitForAvailableSchedulingSlotBlockPracticeType {
  StaticList = 'StaticList',
  Interpolated = 'Interpolated',
}

export enum WaitForAvailableSchedulingSlotBlockProviderUserType {
  StaticList = 'StaticList',
  Interpolated = 'Interpolated',
}

export const WaitForAvailableSchedulingSlotBlockConfigSchema = z
  .object({
    practiceType: z.nativeEnum(WaitForAvailableSchedulingSlotBlockPracticeType),
    practiceIds: z.array(z.string()),
    interpolatedPractice: BlockValueFieldSchema({
      variant: BlockValueFieldSchemaVariant.OptionalTemplate,
    }).optional(),
    providerUserType: z.nativeEnum(WaitForAvailableSchedulingSlotBlockProviderUserType),
    providerUserIds: z.array(z.string()).optional(),
    interpolatedProviderUser: BlockValueFieldSchema({
      variant: BlockValueFieldSchemaVariant.OptionalTemplate,
    }).optional(),
    appointmentKind: z.string().min(1, {
      message: 'Appointment Kind is required',
    }),
    startOffset: BlockValueTemplateDurationSchema({
      pathName: 'startOffset',
      maxDuration: {
        value: 90,
        units: DurationUnits.Days,
      },
    }),
    endOffset: BlockValueTemplateDurationSchema({
      pathName: 'endOffset',
      maxDuration: {
        value: 90,
        units: DurationUnits.Days,
      },
    }),
    priority: z.number().optional().nullable(),
    slotToEntityRatio: z.number().positive({
      message: 'slotToEntityRatio must be a positive number',
    }),
    initialWaitTime: DurationSchemaWithMax('initialWaitTime', {
      value: 1,
      units: DurationUnits.Weeks,
    }),
    initialRetryInterval: DurationSchemaWithMax('initialRetryInterval', {
      value: 1,
      units: DurationUnits.Weeks,
    }),
    maximumAttempts: z.number().int().positive(),
    retryBackoffCoefficient: z.number().positive().min(1),
    maximumRetryInterval: DurationSchemaWithMax('maximumRetryInterval', {
      value: 4,
      units: DurationUnits.Weeks,
    }),
    timeoutInQueue: DurationSchemaWithMax('timeoutInQueue', {
      value: 4,
      units: DurationUnits.Weeks,
    }),
    cacheExpirationDuration: DurationSchemaWithMax('cacheExpirationDuration', {
      value: 24,
      units: DurationUnits.Hours,
    }),
  })
  .refine(
    (data) => {
      const startOffsetNumberValue = Number(data.startOffset.value);
      const endOffsetNumberValue = Number(data.endOffset.value);
      if (isNaN(startOffsetNumberValue) || isNaN(endOffsetNumberValue)) {
        return true;
      }
      return (
        convertDurationToMilliseconds({
          ...data.endOffset,
          value: endOffsetNumberValue,
        }) >
        convertDurationToMilliseconds({
          ...data.startOffset,
          value: startOffsetNumberValue,
        })
      );
    },
    {
      message: "'endOffset' must be greater than 'startOffset'",
      path: ['endOffset'],
    }
  )
  .superRefine((data, context) => {
    switch (data.practiceType) {
      case WaitForAvailableSchedulingSlotBlockPracticeType.StaticList:
        if (data.practiceIds.length > 0) {
          return;
        }
        return context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Practice Ids must have at least one element when Practice Type is Static List',
          path: ['practiceIds'],
        });
      case WaitForAvailableSchedulingSlotBlockPracticeType.Interpolated:
        if (data.interpolatedPractice?.length) {
          return;
        }
        return context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Interpolated Practice must be defined when Practice Type is Interpolated',
          path: ['interpolatedPractice'],
        });
    }
  });

export type WaitForAvailableSchedulingSlotBlockConfig = z.infer<
  typeof WaitForAvailableSchedulingSlotBlockConfigSchema
>;

export const WaitForAvailableSchedulingSlotBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.WaitForAvailableSchedulingSlot),
  config: WaitForAvailableSchedulingSlotBlockConfigSchema,
  returnType: returnTypeSchema(ProducedType.Any),
});

export type WaitForAvailableSchedulingSlotBlock = z.infer<
  typeof WaitForAvailableSchedulingSlotBlockSchema
>;
